import React, { useEffect, useRef, useState } from 'react';
import { SearchIcon, TrashIcon, ArrowPathIcon } from './icons/PlaybackIcons';
import { getCartoTileUrl, computePathDirectionArrows, createDirectionArrowMarker } from '../utils/mapUtils';

declare const L: any;

export interface RaceLocationPickerMapProps {
  startLat: number;
  startLon: number;
  finishLat: number;
  finishLon: number;
  onStartChange: (lat: number, lon: number) => void;
  onFinishChange: (lat: number, lon: number) => void;

  coursePath?: [number, number][];
  rawCoursePath?: [number, number][];
  isTrimmed?: boolean;
  onTrimRoute?: () => void;
  onRevertRoute?: () => void;
  eventType?: 'race' | 'group_ride';
  spiritedStartLat?: number;
  spiritedStartLon?: number;
  spiritedFinishLat?: number;
  spiritedFinishLon?: number;
  onSpiritedStartChange?: (lat: number, lon: number) => void;
  onSpiritedFinishChange?: (lat: number, lon: number) => void;
  onAutoDetect?: () => void;
  isDetecting?: boolean;
}

const findNearestPathPoint = (lat: number, lon: number, path: [number, number][]) => {
  if (!path || path.length === 0) return { lat, lon, index: -1 };
  let minDistSq = Infinity;
  let bestPoint = path[0];
  let bestIdx = 0;
  for (let i = 0; i < path.length; i++) {
    const pt = path[i];
    const dLat = pt[0] - lat;
    const dLon = pt[1] - lon;
    const distSq = dLat * dLat + dLon * dLon;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      bestPoint = pt;
      bestIdx = i;
    }
  }
  return { lat: bestPoint[0], lon: bestPoint[1], index: bestIdx };
};

const getDistanceMeters = (p1: [number, number], p2: [number, number]) => {
  const R = 6371000;
  const dLat = (p2[0] - p1[0]) * Math.PI / 180;
  const dLon = (p2[1] - p1[1]) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(p1[0] * Math.PI / 180) * Math.cos(p2[0] * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const RaceLocationPickerMap: React.FC<RaceLocationPickerMapProps> = ({
  startLat,
  startLon,
  finishLat,
  finishLon,
  onStartChange,
  onFinishChange,
  coursePath,
  rawCoursePath,
  isTrimmed = false,
  onTrimRoute,
  onRevertRoute,
  eventType = 'race',
  spiritedStartLat,
  spiritedStartLon,
  spiritedFinishLat,
  spiritedFinishLon,
  onSpiritedStartChange,
  onSpiritedFinishChange,
  onAutoDetect,
  isDetecting = false,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const polylineRef = useRef<any>(null);
  const untrimmedPolylineRef = useRef<any>(null);
  const routeArrowsGroupRef = useRef<any>(null);
  const spiritedPolylineRef = useRef<any>(null);
  const spiritedArrowsGroupRef = useRef<any>(null);
  const markersRef = useRef<{ start?: any; finish?: any; combo?: any; spiritedStart?: any; spiritedFinish?: any }>({});

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSameLocation, setIsSameLocation] = useState(() => {
    return Boolean(startLat && finishLat && Math.abs(startLat - finishLat) < 0.0001 && Math.abs(startLon - finishLon) < 0.0001 && (startLat !== 0 || startLon !== 0));
  });
  const [activePickerMode, setActivePickerMode] = useState<'start' | 'finish' | 'spirited_start' | 'spirited_finish'>('start');

  const defaultCenter: [number, number] = [47.6062, -122.3321];
  const initialCenter: [number, number] = (startLat && startLon)
    ? [startLat, startLon]
    : (finishLat && finishLon) ? [finishLat, finishLon] : defaultCenter;

  const hasStart = !!(startLat && startLon);
  const hasFinish = !!(finishLat && finishLon);
  const hasSpiritedStart = !!(spiritedStartLat && spiritedStartLon);
  const hasSpiritedFinish = !!(spiritedFinishLat && spiritedFinishLon);
  const hasCoursePath = !!(coursePath && coursePath.length > 0);

  const toggleSameLocation = () => {
    const newState = !isSameLocation;
    setIsSameLocation(newState);
    if (newState) {
      if (hasStart) {
        onFinishChange(startLat, startLon);
      } else if (hasFinish) {
        onStartChange(finishLat, finishLon);
      }
    }
  };

  // Initialize Map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, { zoomControl: false }).setView(initialCenter, 13);
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    L.tileLayer(getCartoTileUrl('rastertiles/voyager'), {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 20
    }).addTo(map);

    mapRef.current = map;

    setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 150);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Update Route Polyline & Fit Bounds
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (polylineRef.current) {
      polylineRef.current.remove();
      polylineRef.current = null;
    }
    if (untrimmedPolylineRef.current) {
      untrimmedPolylineRef.current.remove();
      untrimmedPolylineRef.current = null;
    }
    if (routeArrowsGroupRef.current) {
      routeArrowsGroupRef.current.clearLayers();
    } else {
      routeArrowsGroupRef.current = L.layerGroup().addTo(map);
    }

    // If route was trimmed, render the original commute / untrimmed route in dimmed dashed gray
    if (rawCoursePath && rawCoursePath.length > 0 && coursePath && rawCoursePath.length > coursePath.length) {
      const untrimmedPolyline = L.polyline(rawCoursePath, {
        color: '#64748b', // slate-500
        weight: 3.5,
        opacity: 0.45,
        dashArray: '6, 8',
        lineCap: 'round',
        lineJoin: 'round'
      }).addTo(map);
      untrimmedPolylineRef.current = untrimmedPolyline;
    }

    if (hasCoursePath && coursePath) {
      const polyline = L.polyline(coursePath, {
        color: '#4f46e5', // indigo-600 (matches RaceMap route)
        weight: 5,
        opacity: 0.85,
        lineCap: 'round',
        lineJoin: 'round'
      }).addTo(map);

      polylineRef.current = polyline;
      map.fitBounds(polyline.getBounds().pad(0.12));

      // Render direction arrows along the course path
      const arrows = computePathDirectionArrows(coursePath);
      arrows.forEach(arrow => {
        const marker = createDirectionArrowMarker(L, arrow, {
          color: '#ffffff',
          outlineColor: '#1e1b4b',
          size: 14
        });
        marker.addTo(routeArrowsGroupRef.current);
      });
    }
  }, [coursePath, hasCoursePath, rawCoursePath]);

  // Update Spirited Segment Polyline
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (spiritedPolylineRef.current) {
      spiritedPolylineRef.current.remove();
      spiritedPolylineRef.current = null;
    }
    if (spiritedArrowsGroupRef.current) {
      spiritedArrowsGroupRef.current.clearLayers();
    } else {
      spiritedArrowsGroupRef.current = L.layerGroup().addTo(map);
    }

    if (hasSpiritedStart && hasSpiritedFinish && spiritedStartLat && spiritedStartLon && spiritedFinishLat && spiritedFinishLon) {
      let segmentPath: [number, number][] = [];

      if (hasCoursePath && coursePath) {
        const sNear = findNearestPathPoint(spiritedStartLat, spiritedStartLon, coursePath);
        const fNear = findNearestPathPoint(spiritedFinishLat, spiritedFinishLon, coursePath);

        if (sNear.index !== -1 && fNear.index !== -1) {
          const minIdx = Math.min(sNear.index, fNear.index);
          const maxIdx = Math.max(sNear.index, fNear.index);
          segmentPath = coursePath.slice(minIdx, maxIdx + 1);
        }
      }

      if (segmentPath.length === 0) {
        segmentPath = [[spiritedStartLat, spiritedStartLon], [spiritedFinishLat, spiritedFinishLon]];
      }

      const spiritedPolyline = L.polyline(segmentPath, {
        color: '#06b6d4',
        weight: 7,
        opacity: 0.95,
        lineCap: 'round'
      }).addTo(map);

      spiritedPolylineRef.current = spiritedPolyline;

      // Render direction arrows along the spirited segment path
      const sectorArrows = computePathDirectionArrows(segmentPath, 10);
      sectorArrows.forEach(arrow => {
        const marker = createDirectionArrowMarker(L, arrow, {
          color: '#ffffff',
          outlineColor: '#083344',
          size: 14
        });
        marker.addTo(spiritedArrowsGroupRef.current);
      });
    }
  }, [spiritedStartLat, spiritedStartLon, spiritedFinishLat, spiritedFinishLon, hasSpiritedStart, hasSpiritedFinish, coursePath, hasCoursePath]);

  // Update Markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear previous markers
    if (markersRef.current.start) { markersRef.current.start.remove(); markersRef.current.start = undefined; }
    if (markersRef.current.finish) { markersRef.current.finish.remove(); markersRef.current.finish = undefined; }
    if (markersRef.current.combo) { markersRef.current.combo.remove(); markersRef.current.combo = undefined; }
    if (markersRef.current.spiritedStart) { markersRef.current.spiritedStart.remove(); markersRef.current.spiritedStart = undefined; }
    if (markersRef.current.spiritedFinish) { markersRef.current.spiritedFinish.remove(); markersRef.current.spiritedFinish = undefined; }

    const createIcon = (emoji: string, color: string, glowColor?: string) => L.divIcon({
      className: 'custom-map-picker-icon',
      html: `<div style="background-color: ${color}; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2.5px solid white; box-shadow: ${glowColor ? `0 0 12px ${glowColor}` : '0 4px 6px rgba(0,0,0,0.4)'}; font-size: 18px; cursor: move;">${emoji}</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17]
    });

    if (isSameLocation) {
      const lat = startLat || finishLat;
      const lon = startLon || finishLon;
      if (lat && lon) {
        const marker = L.marker([lat, lon], {
          draggable: true,
          icon: createIcon('🏁', '#a855f7')
        }).addTo(map);
        marker.bindPopup('Start & Finish Line (Loop)');
        marker.on('dragend', (e: any) => {
          const { lat, lng } = e.target.getLatLng();
          const snapped = (hasCoursePath && coursePath) ? findNearestPathPoint(lat, lng, coursePath) : { lat, lon: lng };
          onStartChange(snapped.lat, snapped.lon);
          onFinishChange(snapped.lat, snapped.lon);
        });
        markersRef.current.combo = marker;
      }
    } else {
      if (hasStart) {
        const marker = L.marker([startLat, startLon], {
          draggable: true,
          icon: createIcon('📍', '#10b981')
        }).addTo(map);
        marker.bindPopup('Start / Meetup Location (Drag to move)');
        marker.on('dragend', (e: any) => {
          const { lat, lng } = e.target.getLatLng();
          const snapped = (hasCoursePath && coursePath) ? findNearestPathPoint(lat, lng, coursePath) : { lat, lon: lng };
          onStartChange(snapped.lat, snapped.lon);
        });
        markersRef.current.start = marker;
      }

      if (hasFinish) {
        const marker = L.marker([finishLat, finishLon], {
          draggable: true,
          icon: createIcon('🏁', '#ef4444')
        }).addTo(map);
        marker.bindPopup(eventType === 'group_ride' ? 'Ride End (Drag to move)' : 'Finish Line (Drag to move)');
        marker.on('dragend', (e: any) => {
          const { lat, lng } = e.target.getLatLng();
          const snapped = (hasCoursePath && coursePath) ? findNearestPathPoint(lat, lng, coursePath) : { lat, lon: lng };
          onFinishChange(snapped.lat, snapped.lon);
        });
        markersRef.current.finish = marker;
      }
    }

    // Render Spirited Markers (if group ride)
    if (eventType === 'group_ride') {
      if (hasSpiritedStart && spiritedStartLat && spiritedStartLon && onSpiritedStartChange) {
        const marker = L.marker([spiritedStartLat, spiritedStartLon], {
          draggable: true,
          icon: createIcon('⚡', '#06b6d4', '#06b6d4')
        }).addTo(map);
        marker.bindPopup('⚡ Sector Start (Pace Accelerates)');
        marker.on('dragend', (e: any) => {
          const { lat, lng } = e.target.getLatLng();
          const snapped = (hasCoursePath && coursePath) ? findNearestPathPoint(lat, lng, coursePath) : { lat, lon: lng };
          onSpiritedStartChange(snapped.lat, snapped.lon);
        });
        markersRef.current.spiritedStart = marker;
      }

      if (hasSpiritedFinish && spiritedFinishLat && spiritedFinishLon && onSpiritedFinishChange) {
        const marker = L.marker([spiritedFinishLat, spiritedFinishLon], {
          draggable: true,
          icon: createIcon('🏆', '#06b6d4', '#06b6d4')
        }).addTo(map);
        marker.bindPopup('🏁 Sector End / Sprint Line');
        marker.on('dragend', (e: any) => {
          const { lat, lng } = e.target.getLatLng();
          const snapped = (hasCoursePath && coursePath) ? findNearestPathPoint(lat, lng, coursePath) : { lat, lon: lng };
          onSpiritedFinishChange(snapped.lat, snapped.lon);
        });
        markersRef.current.spiritedFinish = marker;
      }
    }
  }, [startLat, startLon, finishLat, finishLon, spiritedStartLat, spiritedStartLon, spiritedFinishLat, spiritedFinishLon, isSameLocation, hasStart, hasFinish, hasSpiritedStart, hasSpiritedFinish, eventType, coursePath, hasCoursePath]);

  // Click Handler for placing active markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onMapClick = (e: any) => {
      const clickLat = e.latlng.lat;
      const clickLon = e.latlng.lng;
      const snapped = (hasCoursePath && coursePath)
        ? findNearestPathPoint(clickLat, clickLon, coursePath)
        : { lat: clickLat, lon: clickLon, index: -1 };

      if (activePickerMode === 'start') {
        if (isSameLocation) {
          onStartChange(snapped.lat, snapped.lon);
          onFinishChange(snapped.lat, snapped.lon);
        } else {
          onStartChange(snapped.lat, snapped.lon);
          if (eventType === 'group_ride' && onSpiritedStartChange && !hasSpiritedStart) {
            setActivePickerMode('spirited_start');
          } else if (!hasFinish) {
            setActivePickerMode('finish');
          }
        }
      } else if (activePickerMode === 'finish') {
        onFinishChange(snapped.lat, snapped.lon);
      } else if (activePickerMode === 'spirited_start' && onSpiritedStartChange) {
        onSpiritedStartChange(snapped.lat, snapped.lon);
        if (!hasSpiritedFinish) {
          setActivePickerMode('spirited_finish');
        }
      } else if (activePickerMode === 'spirited_finish' && onSpiritedFinishChange) {
        onSpiritedFinishChange(snapped.lat, snapped.lon);
      }
    };

    map.on('click', onMapClick);
    return () => { map.off('click', onMapClick); };
  }, [activePickerMode, isSameLocation, hasCoursePath, coursePath, eventType, hasStart, hasFinish, hasSpiritedStart, hasSpiritedFinish, onStartChange, onFinishChange, onSpiritedStartChange, onSpiritedFinishChange]);

  // Calculate Spirited Distance in Miles
  let spiritedMilesStr: string | null = null;
  if (hasSpiritedStart && hasSpiritedFinish && spiritedStartLat && spiritedStartLon && spiritedFinishLat && spiritedFinishLon) {
    if (hasCoursePath && coursePath) {
      const sNear = findNearestPathPoint(spiritedStartLat, spiritedStartLon, coursePath);
      const fNear = findNearestPathPoint(spiritedFinishLat, spiritedFinishLon, coursePath);
      if (sNear.index !== -1 && fNear.index !== -1) {
        let meters = 0;
        const minIdx = Math.min(sNear.index, fNear.index);
        const maxIdx = Math.max(sNear.index, fNear.index);
        for (let i = minIdx; i < maxIdx; i++) {
          meters += getDistanceMeters(coursePath[i], coursePath[i + 1]);
        }
        spiritedMilesStr = (meters * 0.000621371).toFixed(2);
      }
    } else {
      const meters = getDistanceMeters([spiritedStartLat, spiritedStartLon], [spiritedFinishLat, spiritedFinishLon]);
      spiritedMilesStr = (meters * 0.000621371).toFixed(2);
    }
  }

  // Address search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setSearchError(null);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
      if (!response.ok) throw new Error('Search failed');
      const data = await response.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        if (mapRef.current) mapRef.current.flyTo([lat, lon], 14, { duration: 1.5 });
      } else {
        setSearchError('Address not found.');
      }
    } catch {
      setSearchError('Connection failed.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSearch();
    }
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Map Section */}
      <div className="relative w-full h-[360px] sm:h-[400px] rounded-lg overflow-hidden border border-gray-600 bg-gray-900 shadow-inner">
        <div ref={mapContainerRef} className="w-full h-full" />

        {/* Search Overlay */}
        <div className="absolute top-2 left-2 z-[1000] flex flex-col gap-1 w-64 max-w-[75%]">
          <div className="flex shadow-lg rounded-md overflow-hidden border border-gray-600">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search address or city..."
              className="flex-grow px-3 py-1.5 bg-gray-800 text-white text-xs focus:outline-none placeholder-gray-400"
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={isSearching}
              className="px-2.5 bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-50 flex items-center justify-center transition-colors"
            >
              {isSearching ? '...' : <SearchIcon className="w-3.5 h-3.5" />}
            </button>
          </div>
          {searchError && (
            <div className="bg-red-900/90 text-red-200 text-[10px] px-2 py-1 rounded shadow-md">
              {searchError}
            </div>
          )}
        </div>
      </div>

      {/* Controls Container (Placed directly below map) */}
      <div className="bg-gray-800/95 p-3 rounded-lg border border-gray-600 shadow-md text-xs space-y-2.5">
        <div className="flex justify-between items-center border-b border-gray-700 pb-1.5">
          <span className="font-bold text-gray-200 text-[11px] uppercase tracking-wider flex items-center gap-1.5">
            <span>🗺️</span> Map & Route Tools
          </span>
          {hasCoursePath && (
            <span className="text-[10px] text-cyan-300 bg-cyan-950/80 px-2 py-0.5 rounded border border-cyan-700/50 font-medium">
              GPX Snapping Active
            </span>
          )}
        </div>

        {/* Mode Selector Buttons */}
        <div>
          <span className="block text-[10px] text-gray-400 font-medium mb-1">Click map to place:</span>
          <div className={`grid ${eventType === 'group_ride' ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2'} gap-1.5`}>
            <button
              type="button"
              onClick={() => setActivePickerMode('start')}
              className={`px-2 py-1.5 text-[11px] font-bold rounded border transition-all flex items-center justify-center gap-1 ${
                activePickerMode === 'start'
                  ? 'bg-emerald-600 text-white border-emerald-400 ring-2 ring-emerald-500/50 shadow-md'
                  : 'bg-gray-700/80 text-gray-300 border-gray-600 hover:text-white hover:bg-gray-700'
              }`}
            >
              📍 Meetup/Start
            </button>
            <button
              type="button"
              onClick={() => setActivePickerMode('finish')}
              className={`px-2 py-1.5 text-[11px] font-bold rounded border transition-all flex items-center justify-center gap-1 ${
                activePickerMode === 'finish'
                  ? 'bg-red-600 text-white border-red-400 ring-2 ring-red-500/50 shadow-md'
                  : 'bg-gray-700/80 text-gray-300 border-gray-600 hover:text-white hover:bg-gray-700'
              }`}
            >
              🏁 {eventType === 'group_ride' ? 'Ride End' : 'Finish Line'}
            </button>

            {eventType === 'group_ride' && (
              <>
                <button
                  type="button"
                  onClick={() => setActivePickerMode('spirited_start')}
                  className={`px-2 py-1.5 text-[11px] font-bold rounded border transition-all flex items-center justify-center gap-1 ${
                    activePickerMode === 'spirited_start'
                      ? 'bg-cyan-600 text-white border-cyan-400 ring-2 ring-cyan-500/50 shadow-md'
                      : 'bg-gray-700/80 text-cyan-300 border-cyan-900/60 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  ⚡ Sector Start
                </button>
                <button
                  type="button"
                  onClick={() => setActivePickerMode('spirited_finish')}
                  className={`px-2 py-1.5 text-[11px] font-bold rounded border transition-all flex items-center justify-center gap-1 ${
                    activePickerMode === 'spirited_finish'
                      ? 'bg-cyan-600 text-white border-cyan-400 ring-2 ring-cyan-500/50 shadow-md'
                      : 'bg-gray-700/80 text-cyan-300 border-cyan-900/60 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  🏆 Sector End
                </button>
              </>
            )}
          </div>
        </div>

        {spiritedMilesStr && eventType === 'group_ride' && (
          <div className="p-1.5 bg-cyan-950/60 rounded border border-cyan-600/40 text-[10px] text-cyan-200 flex justify-between items-center font-semibold">
            <span>⚡ Sector Distance:</span>
            <span className="font-bold text-white text-xs">{spiritedMilesStr} mi</span>
          </div>
        )}

        <div className="pt-1.5 border-t border-gray-700/80 space-y-1.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={toggleSameLocation}
              className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] font-bold rounded border transition-all ${
                isSameLocation ? 'bg-purple-600 text-white border-purple-500 shadow-sm' : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
              }`}
            >
              <ArrowPathIcon className="w-3.5 h-3.5" />
              {isSameLocation ? 'Loop Course Enabled' : 'Enable Loop Course'}
            </button>

            {hasCoursePath && onTrimRoute && hasStart && hasFinish && (
              <button
                type="button"
                onClick={onTrimRoute}
                className={`w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-bold rounded border shadow-sm transition-all ${
                  isTrimmed
                    ? 'bg-emerald-950/80 text-emerald-300 border-emerald-600/60 hover:bg-emerald-900/80'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-400 ring-1 ring-emerald-400'
                }`}
                title="Trim route between Start/Meetup and Finish points"
              >
                <span>✂️</span> {isTrimmed ? 'Re-Trim to Markers' : 'Trim Route to Start & End'}
              </button>
            )}
          </div>

          {hasCoursePath && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {isTrimmed && onRevertRoute && (
                <button
                  type="button"
                  onClick={onRevertRoute}
                  className="w-full flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-gray-600 bg-gray-700/80 hover:bg-gray-700 text-gray-200 transition-colors"
                  title="Revert back to the untrimmed original file"
                >
                  <span>↺</span> Revert to Full Route
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  const s = (rawCoursePath && rawCoursePath.length > 0 ? rawCoursePath : coursePath)[0];
                  const f = (rawCoursePath && rawCoursePath.length > 0 ? rawCoursePath : coursePath)[(rawCoursePath && rawCoursePath.length > 0 ? rawCoursePath : coursePath).length - 1];
                  onStartChange(s[0], s[1]);
                  onFinishChange(f[0], f[1]);
                }}
                className="w-full flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-cyan-800/60 bg-cyan-950/50 hover:bg-cyan-900/60 text-cyan-300 transition-colors"
                title="Reset meetup and finish points to the route file endpoints"
              >
                <span>↺</span> Revert to File Start & End
              </button>
            </div>
          )}
        </div>

        <div className={`grid ${onAutoDetect ? 'grid-cols-3' : 'grid-cols-2'} gap-1.5 pt-1.5 border-t border-gray-700/80`}>
          {onAutoDetect && (
            <button
              type="button"
              onClick={onAutoDetect}
              disabled={isDetecting}
              className="flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-bold rounded border border-cyan-700 bg-cyan-900/60 hover:bg-cyan-800/80 text-cyan-200 transition-colors disabled:opacity-50"
            >
              <span>⚡</span> {isDetecting ? 'Detecting...' : 'Auto-Detect'}
            </button>
          )}
          <button
            type="button"
            onClick={() => onStartChange(0, 0)}
            disabled={!hasStart}
            className={`flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-bold rounded border transition-colors ${
              hasStart ? 'bg-gray-700 hover:bg-red-900/30 text-gray-200 border-gray-500' : 'bg-gray-800 text-gray-600 border-gray-700 cursor-not-allowed'
            }`}
          >
            <TrashIcon className="w-3 h-3" />
            Reset Start
          </button>
          <button
            type="button"
            onClick={() => onFinishChange(0, 0)}
            disabled={!hasFinish}
            className={`flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-bold rounded border transition-colors ${
              hasFinish ? 'bg-gray-700 hover:bg-red-900/30 text-gray-200 border-gray-500' : 'bg-gray-800 text-gray-600 border-gray-700 cursor-not-allowed'
            }`}
          >
            <TrashIcon className="w-3 h-3" />
            Reset Finish
          </button>
        </div>
      </div>
    </div>
  );
};

export default RaceLocationPickerMap;